import { Button } from '@wayste/shadcn/components/button';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink } from '@wayste/shadcn/components/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@wayste/shadcn/components/select';
import { type SourTablePagination } from '@wayste/shadcn/types/sour-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type SourTablePaginationProps = {
    pagination: SourTablePagination;
};

export const SourTablePaginationComponent = ({ pagination }: SourTablePaginationProps) => {
    const { page, pageSize, pageSizeOptions, total, onPageChange, onPageSizeChange } = pagination;

    const startOfRange = Math.max(1, page * pageSize + 1);
    const endOfRange = Math.min(total, (page + 1) * pageSize);
    const totalPages = Math.ceil(total / pageSize);
    const currentPage = page + 1; // Convert to 1-based for display

    const generatePageNumbers = (isMobile = false) => {
        const pages: (number | 'ellipsis')[] = [];
        const maxPagesToShow = isMobile ? 3 : 5;

        if (totalPages <= maxPagesToShow) {
            // Show all pages if within the limit
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (isMobile) {
                // Mobile: Show current page and neighbors (max 3 pages)
                if (currentPage === 1) {
                    pages.push(1, 2);
                    if (totalPages > 2) {
                        pages.push('ellipsis');
                        pages.push(totalPages);
                    }
                } else if (currentPage === totalPages) {
                    pages.push(1);
                    pages.push('ellipsis');
                    if (totalPages > 2) {
                        pages.push(totalPages - 1);
                    }
                    pages.push(totalPages);
                } else {
                    // Show current page with one neighbor on each side
                    if (currentPage > 2) {
                        pages.push(1);
                        if (currentPage > 3) {
                            pages.push('ellipsis');
                        }
                    }

                    if (currentPage > 1) {
                        pages.push(currentPage - 1);
                    }
                    pages.push(currentPage);
                    if (currentPage < totalPages) {
                        pages.push(currentPage + 1);
                    }

                    if (currentPage < totalPages - 1) {
                        if (currentPage < totalPages - 2) {
                            pages.push('ellipsis');
                        }
                        pages.push(totalPages);
                    }
                }
            } else {
                // Desktop: Keep existing logic
                // Always show first page
                pages.push(1);

                // Determine the rolling window around current page
                let startWindow: number;
                let endWindow: number;

                if (currentPage === 1) {
                    // On first page - show [1, 2, 3, ..., last]
                    startWindow = 2;
                    endWindow = 3;
                } else if (currentPage === totalPages) {
                    // On last page - show [1, ..., last-2, last-1, last]
                    startWindow = totalPages - 2;
                    endWindow = totalPages - 1;
                } else {
                    // Show current page with 2 neighbors on each side
                    startWindow = Math.max(2, currentPage - 2);
                    endWindow = Math.min(totalPages - 1, currentPage + 2);
                }

                // Add ellipsis after first page if needed
                if (startWindow > 2) {
                    pages.push('ellipsis');
                }

                // Add the rolling window pages
                for (let i = startWindow; i <= endWindow; i++) {
                    if (i > 1 && i < totalPages) {
                        pages.push(i);
                    }
                }

                // Add ellipsis before last page if needed
                if (endWindow < totalPages - 1) {
                    pages.push('ellipsis');
                }

                // Always show last page
                if (totalPages > 1) {
                    pages.push(totalPages);
                }
            }
        }

        return pages;
    };

    const desktopPageNumbers = generatePageNumbers(false);
    const mobilePageNumbers = generatePageNumbers(true);

    const handlePageClick = (pageNum: number) => {
        onPageChange(pageNum - 1); // Convert back to 0-based
    };

    return (
        <>
            {/* Mobile Layout - Stacked */}
            <div className="bg-surface-secondary flex flex-col gap-3 border-t p-3 sm:hidden">
                {/* Top row: Range and page size */}
                <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-xs">
                        {totalPages === 0 ? 'No results' : `${startOfRange}-${endOfRange} of ${total}`}
                    </span>
                    <div className="flex items-center gap-2">
                        <span className="text-text-secondary text-xs">Rows:</span>
                        <Select value={String(pageSize)} onValueChange={(val) => onPageSizeChange(Number(val))}>
                            <SelectTrigger size="sm" className="h-7 w-14 text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeOptions.map((size) => (
                                    <SelectItem key={size} value={String(size)}>
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Bottom row: Pagination controls */}
                <div className="flex justify-center">
                    <Pagination className="mx-0 w-auto">
                        <PaginationContent className="gap-0">
                            <PaginationItem>
                                <Button
                                    variant="tertiary"
                                    size="icon"
                                    onClick={() => handlePageClick(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    aria-label="Go to previous page"
                                    className="size-8"
                                >
                                    <ChevronLeft className="size-4" />
                                </Button>
                            </PaginationItem>

                            {mobilePageNumbers.map((pageNum, index) => (
                                <PaginationItem key={index}>
                                    {pageNum === 'ellipsis' ? (
                                        <span className="px-1 text-xs">...</span>
                                    ) : (
                                        <PaginationLink
                                            onClick={() => handlePageClick(pageNum)}
                                            isActive={pageNum === currentPage}
                                            className="size-8 cursor-pointer text-xs"
                                        >
                                            {pageNum}
                                        </PaginationLink>
                                    )}
                                </PaginationItem>
                            ))}

                            <PaginationItem>
                                <Button
                                    variant="tertiary"
                                    size="icon"
                                    onClick={() => handlePageClick(currentPage + 1)}
                                    disabled={currentPage === totalPages && totalPages === 0}
                                    aria-label="Go to next page"
                                    className="size-8"
                                >
                                    <ChevronRight className="size-4" />
                                </Button>
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            </div>

            {/* Desktop Layout - Range on left, Rows per page and Pagination on right */}
            <div className="bg-surface-secondary hidden items-center justify-between gap-4 border-t px-4 py-3 sm:flex">
                {/* Left: Range display */}
                <div className="flex items-center justify-start">
                    <span className="text-text-secondary text-sm">
                        {totalPages === 0 ? 'No results' : `${startOfRange}-${endOfRange} of ${total}`}
                    </span>
                </div>

                {/* Right: Page size selector and Pagination controls */}
                <div className="flex items-center gap-6">
                    {/* Page size selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-text-secondary text-sm">Rows per page:</span>
                        <Select value={String(pageSize)} onValueChange={(val) => onPageSizeChange(Number(val))}>
                            <SelectTrigger size="sm" className="w-20">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {pageSizeOptions.map((size) => (
                                    <SelectItem key={size} value={String(size)}>
                                        {size}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex">
                        <Pagination className="mx-0 w-auto">
                            <PaginationContent>
                                <PaginationItem>
                                    <Button
                                        variant="tertiary"
                                        size="icon"
                                        onClick={() => handlePageClick(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        aria-label="Go to previous page"
                                        className="size-9"
                                    >
                                        <ChevronLeft className="size-4" />
                                    </Button>
                                </PaginationItem>

                                {desktopPageNumbers.map((pageNum, index) => (
                                    <PaginationItem key={index}>
                                        {pageNum === 'ellipsis' ? (
                                            <PaginationEllipsis />
                                        ) : (
                                            <PaginationLink
                                                onClick={() => handlePageClick(pageNum)}
                                                isActive={pageNum === currentPage}
                                                className="size-9 cursor-pointer"
                                            >
                                                {pageNum}
                                            </PaginationLink>
                                        )}
                                    </PaginationItem>
                                ))}

                                <PaginationItem>
                                    <Button
                                        variant="tertiary"
                                        size="icon"
                                        onClick={() => handlePageClick(currentPage + 1)}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        aria-label="Go to next page"
                                        className="size-9"
                                    >
                                        <ChevronRight className="size-4" />
                                    </Button>
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                </div>
            </div>
        </>
    );
};
